import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue } from "framer-motion";
import { Flame, Stamp } from "lucide-react";

function SealHold({ d, onOpen }) {
  const [p, setP] = useState(0);
  const t = useRef(null);
  const start = () => { clearInterval(t.current); t.current = setInterval(() => setP((x) => { if (x >= 100) { clearInterval(t.current); onOpen(); return 100; } return x + 5; }), 50); };
  const stop = () => { clearInterval(t.current); setP((x) => (x >= 100 ? x : 0)); };
  useEffect(() => () => clearInterval(t.current), []);
  return (
    <button onPointerDown={start} onPointerUp={stop} onPointerLeave={stop} className="relative w-24 h-24 rounded-full flex items-center justify-center select-none touch-none" style={{ background: `radial-gradient(circle, #a51d2d, #5e0a14)`, boxShadow: "0 6px 20px #0008" }} data-testid="gate-seal">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100"><circle cx="50" cy="50" r="46" fill="none" stroke={d.pal.accent} strokeWidth="4" strokeDasharray={289} strokeDashoffset={289 - (289 * p) / 100} /></svg>
      <motion.span animate={p > 0 ? { rotate: [0, -6, 6, 0] } : {}} transition={{ repeat: Infinity, duration: 0.3 }}><Stamp className="w-9 h-9 text-[#f3d0a0]" /></motion.span>
    </button>
  );
}

function Scratch({ d, label, onOpen }) {
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const c = ref.current, x = c.getContext("2d");
    const g = x.createLinearGradient(0, 0, c.width, c.height);
    g.addColorStop(0, "#b8893b"); g.addColorStop(0.5, "#f3d98f"); g.addColorStop(1, "#a07428");
    x.fillStyle = g; x.fillRect(0, 0, c.width, c.height);
    x.fillStyle = "#5a3f12"; x.font = "14px serif"; x.textAlign = "center"; x.fillText("scratch here", c.width / 2, c.height / 2 + 5);
  }, []);
  function scratch(e) {
    if (e.buttons === 0 && e.pointerType === "mouse") return;
    const c = ref.current, r = c.getBoundingClientRect(), x = c.getContext("2d");
    x.globalCompositeOperation = "destination-out"; x.beginPath();
    x.arc(((e.clientX - r.left) / r.width) * c.width, ((e.clientY - r.top) / r.height) * c.height, 18, 0, Math.PI * 2); x.fill();
    const px = x.getImageData(0, 0, c.width, c.height).data;
    let clear = 0; for (let i = 3; i < px.length; i += 64) if (px[i] === 0) clear++;
    if (!done.current && clear / (px.length / 64) > 0.45) { done.current = true; setTimeout(onOpen, 400); }
  }
  return (
    <div className="relative w-56 h-24 rounded-xl overflow-hidden border" style={{ borderColor: d.pal.accent, background: d.pal.panel }} data-testid="gate-scratch">
      <p className="absolute inset-0 flex items-center justify-center text-lg" style={{ color: d.pal.text, fontFamily: "'Playfair Display', serif" }}>{label}</p>
      <canvas ref={ref} width={224} height={96} onPointerMove={scratch} onPointerDown={scratch} className="absolute inset-0 w-full h-full touch-none cursor-crosshair" data-testid="gate-scratch-canvas" />
    </div>
  );
}

function Swipe({ d, onOpen }) {
  const x = useMotionValue(0);
  return (
    <div className="relative w-60 h-14 rounded-full border overflow-hidden" style={{ borderColor: d.pal.accent, background: `${d.pal.panel}cc` }} data-testid="gate-swipe">
      <span className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.3em] animate-pulse" style={{ color: d.pal.accent }}>Swipe to open →</span>
      <motion.button drag="x" dragConstraints={{ left: 0, right: 184 }} dragElastic={0} dragMomentum={false} style={{ x, background: d.pal.accent }} onDragEnd={() => (x.get() > 140 ? onOpen() : x.set(0))}
        onKeyDown={(e) => e.key === "Enter" && onOpen()} className="absolute left-1 top-1 w-12 h-12 rounded-full shadow-lg" aria-label="Swipe to open" data-testid="gate-swipe-handle" />
    </div>
  );
}

function Candles({ d, onOpen }) {
  const [lit, setLit] = useState([false, false, false]);
  function light(i) { const n = lit.map((v, j) => v || j === i); setLit(n); if (n.every(Boolean)) setTimeout(onOpen, 700); }
  return (
    <div className="flex items-end gap-5" data-testid="gate-candles">
      {lit.map((on, i) => (
        <button key={i} onClick={() => light(i)} className="flex flex-col items-center" aria-label={`Light candle ${i + 1}`} data-testid={`gate-candle-${i}`}>
          <motion.span animate={on ? { scale: [1, 1.15, 1], opacity: 1 } : { opacity: 0.2 }} transition={on ? { repeat: Infinity, duration: 1.2 } : {}} style={{ color: "#ffb547", filter: on ? "drop-shadow(0 0 10px #ffb547)" : "none" }}><Flame className="w-6 h-6" /></motion.span>
          <span className="w-4 rounded-sm" style={{ height: 40 + i * 12, background: `linear-gradient(180deg, #fffaf0, ${d.pal.panel})` }} />
        </button>
      ))}
    </div>
  );
}

export function Gate({ kind, d, dateLabel, onOpen }) {
  if (kind === "wax-seal") return <SealHold d={d} onOpen={onOpen} />;
  if (kind === "scratch") return <Scratch d={d} label={dateLabel || "Forever"} onOpen={onOpen} />;
  if (kind === "swipe") return <Swipe d={d} onOpen={onOpen} />;
  if (kind === "candles") return <Candles d={d} onOpen={onOpen} />;
  return null;
}

export const GATE_CTA = { "wax-seal": "Press and hold the seal", scratch: "Scratch the gold card", swipe: "Swipe to open", candles: "Light all three candles" };
